import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../TextField/TextFeild";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import moment from "moment";
import { createContest, startContest } from "../../store/slices/contestSlice";
import { useSocket } from "./../../context/SocketContext";
import {
    FaPlus,
    FaImage,
    FaTimes
} from "react-icons/fa";

// Import CKEditor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CropperModalAdmin from "./CropperModalAdmin";
import { HUMAN_TIMEZONES } from "../../constants";


const CreateContest = ({ show, onClose }) => {
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const { socket } = useSocket();

    const roomId = auth?.user?.attributes?.stationChatId;

    const [step, setStep] = useState(1);
    const [answers, setAnswers] = useState(["", ""]);
    const [followersOnly, setFollowersOnly] = useState(false);
    const [showQuestions, setShowQuestions] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [image, setImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [description, setDescription] = useState(""); // State for CKEditor content
    const [deletedAnswers, setDeletedAnswers] = useState([]);
    const [isImageDeleted, setIsImageDeleted] = useState(false);

    const validationSchema = Yup.object().shape({
        prize: Yup.string().required("Prize is required").max(200, "Title cannot be longer than 200 characters"),
        nWinners: Yup.number()
            .required("Number of winners is required")
            .positive("Must be positive")
            .integer("Must be integer"),
            rules: Yup.string().max(200, "Title cannot be longer than 200 characters"),
        ...(showQuestions && {
            title: Yup.string()
                .max(200, "Must be 200 characters or less")
                .required("Question is required"),
            answers: Yup.array()
                .of(Yup.string().required("Answer is required").max(200, "Title cannot be longer than 200 characters"))
                .min(2, "At least 2 answers required")
                .required("Answers required")
        }),
        tz: Yup.string(),

        startDate: Yup.string().when("tz", {
          is: (tz) => tz && tz.length > 0,
          then: (schema) => schema.required("Start date is required when timezone is selected"),
        }),
    
        startTime: Yup.string().when("tz", {
          is: (tz) => tz && tz.length > 0,
          then: (schema) =>
            schema
              .required("Start time is required when timezone is selected")
              .test(
                "is-future-5-min",
                "Start time must be at least 5 minutes in the future",
                function (value) {
                  let { startDate, tz } = this.parent;
                  tz = HUMAN_TIMEZONES[tz];
    
                  if (!startDate || !value || !tz) return true;
    
                  // Convert to moment object
                  const start = moment.tz(
                    `${startDate} ${value}`,
                    "YYYY-MM-DD HH:mm",
                    tz
                  );
                  const now = moment.tz(tz);
                  const minutesUntil = start.diff(now, 'minutes', true);
    
                  console.log("timezone:", tz);
                  console.log("startInTZ:", start.format());
                  console.log("now:", now.format());
                  console.log("minutesUntil:", minutesUntil);
    
                  // Validation
                  if (minutesUntil < 5) {
                    return this.createError({
                      message: `Start Time must be 5 minutes in advance.`
                    });
                  }
                  
                  return true;
                }
              ),
        }),
    
        endDate: Yup.string().when("tz", {
          is: (tz) => tz && tz.length > 0,
          then: (schema) => schema.required("End date is required when timezone is selected"),
        }),
    
        endTime: Yup.string().when(["tz", "startDate", "startTime"], {
          is: (tz, sd, st) => tz && sd && st,
          then: (schema) =>
            schema
              .required("End time is required when timezone is selected")
              .test(
                "is-after-start",
                "End time must be after the start time",
                function (value) {
                  let { startDate, startTime, endDate, tz } = this.parent;
                  tz = HUMAN_TIMEZONES[tz];
                  if (!startDate || !startTime || !endDate || !value || !tz)
                    return true;
    
                  const start = moment.tz(
                    `${startDate} ${startTime}`,
                    "YYYY-MM-DD HH:mm",
                    tz
                  );
    
                  const end = moment.tz(
                    `${endDate} ${value}`,
                    "YYYY-MM-DD HH:mm",
                    tz
                  );
    
                  return end.isAfter(start);
                }
              ),
        }),

    });

    const handleCrop = (croppedImg) => {
        console.log(croppedImg , 'croppedImg')
        setCroppedImage(croppedImg);
        formik.setFieldValue("image", croppedImg);
        setShowModal(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setShowModal(true);
            };
            reader.readAsDataURL(file);
        }
    };
    const formik = useFormik({
        initialValues: {
            title: "",
            answers: ["", ""],
            tz: "",
            startDate: "",
            startTime: "",
            endDate: "",
            endTime: "",
            prize: "",
            nWinners: "",
            rules: "",
            image: "",
            description: "", // Add description to form values
        },
        validationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        onSubmit: async (values, { resetForm }) => {
            if (step === 1) {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                    setStep(2);
                }
            } else {
                const { tz } = values;

                // Filter out deleted answers
                const activeAnswers = values.answers ? values.answers.filter((_, index) => !deletedAnswers.includes(index)) : [];
                
                dispatch(createContest({
                    data: {
                        ...values,
                        answers: activeAnswers,
                        image: isImageDeleted ? "" : values.image,
                        followersOnly,
                        hasQuestions: showQuestions,
                    },
                }))
                    .then((data) => {
                        const pollId = data.payload.id;
                        if (!tz) {
                            return dispatch(startContest({ id: pollId })).then(() => {
                                socket.emit(`poll.contest.started`, { contest: data.payload, roomId });
                            });
                        }
                        return true;
                    })
                    .then(() => {
                        resetForm();
                        setStep(1);
                        setAnswers(["", ""]);
                        setShowQuestions(false);
                        setCroppedImage(null);
                        setFollowersOnly(false);
                        onClose();
                    });
            }
        },
    });

    useEffect(() => {
        if (show) {
            formik.resetForm();
            setAnswers(["", ""]);
            setStep(1);
            setCroppedImage(null);
            setShowQuestions(false);
            setFollowersOnly(false);
            setDeletedAnswers([]);
            setIsImageDeleted(false);
        }
    }, [show]);

    const handleAnswerChange = (index, value) => {
        const newAnswers = [...formik.values.answers];
        newAnswers[index] = value;
        formik.setFieldValue("answers", newAnswers);
    };

    const addAnswer = () => {
        if (formik.values.answers.length < 4) {
            formik.setFieldValue("answers", [...formik.values.answers, ""]);
        }
    };
    const setFormikDescription = (data) => {  
            formik.setFieldValue("description", data);

    };


    const handleDeleteAnswer = (index) => {
        setDeletedAnswers([...deletedAnswers, index]);
    };

    const handleUndoDeleteAnswer = (index) => {
        setDeletedAnswers(deletedAnswers.filter((i) => i !== index));
    };

    const removeAnswer = (index) => {
        const newAnswers = formik.values.answers.filter((_, i) => i !== index);
        formik.setFieldValue("answers", newAnswers);
        // Remove from deletedAnswers if it was there
        setDeletedAnswers(deletedAnswers.filter((i) => i !== index).map(i => i > index ? i - 1 : i));
    };

    const handleDeleteImage = () => {
        setIsImageDeleted(true);
    };

    const handleUndoDeleteImage = () => {
        setIsImageDeleted(false);
    };

    return (
        <>
            <Modal show={show}
                size="xl" // Increased size to accommodate CKEditor better
                onHide={onClose}
                centered
                backdrop="static"
                keyboard={false}>
                <form onSubmit={formik.handleSubmit} className="row m-3">
                    <div className="col-md-12 " style={{ borderBottom: "1px solid grey" }}>
                        <div className="logo-cener">
                            <svg viewBox="0 0 24 24" className="status-icon">
                                <circle cx="12" cy="12" r="12" fill="#4A90E2"></circle>
                                <path
                                    fill="white"
                                    d="M17.9 9.7v-.8h-2.1c0 1.2-.2 2.2-.6 3.1.8-.2 1.4-.5 2-.9.4-.5.7-1 .7-1.4 0 0 0 .4 0 0zm-9 2.3c-.4-.9-.6-1.9-.6-3.1H6.1v.8c0 .4.3.9.8 1.4.5.4 1.2.7 2 .9 0 0-.8-.2 0 0zM19 8.6v1.1c0 .4-.1.8-.3 1.2-.2.4-.5.8-.9 1.1-.4.3-.9.6-1.5.8-.6.2-1.2.3-1.8.4-.2.3-.5.6-.8.8-.2.2-.4.4-.4.6-.1.2-.1.5-.1.7 0 .3.1.6.3.8.2.2.4.3.8.3s.8.1 1.1.4c.3.3.5.6.5 1v.5c0 .1 0 .1-.1.2s-.1.1-.2.1h-7c-.1 0-.1 0-.2-.1s-.1-.1-.1-.2v-.5c0-.4.2-.7.5-1 .3-.3.7-.4 1.1-.4.4 0 .6-.1.8-.3.2-.2.3-.5.3-.8 0-.3 0-.5-.1-.7s-.2-.4-.4-.6c-.3-.2-.6-.5-.8-.8-.6 0-1.2-.2-1.8-.4-.6-.2-1.1-.5-1.5-.8-.4-.3-.7-.7-.9-1.1-.4-.4-.5-.8-.5-1.2V8.6c0-.2.1-.4.2-.6.2-.2.3-.2.6-.2h2.4V7c0-.4.1-.7.4-.9.3-.3.6-.4.9-.4h4.8c.4 0 .7.1.9.4.3.3.4.6.4.9v.8H18c.2 0 .4.1.6.2.3.2.4.4.4.6 0 0 0-.2 0 0z"
                                ></path>
                            </svg>
                        </div>
                        {step === 1 && (
                            <div className="row mb-3">
                                <div className="col-md-6 d-flex align-items-center">

                                    <p className="h4 mb-0">Create Contest</p>
                                </div>

                                <div className="col-md-12">
                                    <p className="text-muted mb-3">
                                        You can create a contest and start it. You will be able to alter it and manually start and stop it.
                                    </p>
                                </div>
                            </div>
                        )
                        }
                        {step === 2 && (
                            <>
                                <h4 className="mb-2">Start Contest Now or Schedule for Later</h4>
                                <p className="text-muted mb-3">
                                    You can schedule Contest to begin at your preferred time and date in the future.
                                </p>
                            </>
                        )}

                    </div>
                    <div className="col-md-6 shadow-none p-3 m-0" style={{ borderRight: "1px solid grey" }}>
                        {step === 1 && (
                            <>


                                <div className="px-2">
                                    <div className="row">
                                        <div className="col-md-5">
                                            <div className=" mb-2 d-flex justify-content-start align-items-center gap-3">
                                                <label className="form-label fw-bold mb-0">Followers Only</label>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={followersOnly}
                                                        onChange={() => setFollowersOnly(!followersOnly)}
                                                    />
                                                </div>
                                            </div>
                                            
                                        </div>
                                        <div className="col-md-7 d-flex justify-content-end align-items-center">
                                                <span> <strong>No Question</strong></span>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={showQuestions}
                                                        onChange={() => setShowQuestions(!showQuestions)}
                                                    />
                                                </div>
                                                <span><strong>With a Question</strong></span>
                                            </div>
                                    </div>
                                    <CustomInput
                                        label="prize"
                                        id="prize"
                                        name="prize"
                                        value={formik.values.prize}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.prize && formik.errors.prize}
                                        required
                                    />

                                    <CustomInput
                                        label="Number of Winners"
                                        id="nWinners"
                                        name="nWinners"
                                        value={formik.values.nWinners}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.nWinners && formik.errors.nWinners}
                                        required
                                    />
                                    <CustomInput
                                        label="Rules"
                                        id="rules"
                                        name="rules"
                                        isTextArea={true}
                                        value={formik.values.rules}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.rules && formik.errors.rules}
                                    />
                                    {/* Conditionally render Question and Answer Fields */}
                                    {showQuestions && (
                                        <>
                                            <CustomInput
                                                label="Question"
                                                id="title"
                                                name="title"
                                                value={formik.values.title}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.title && formik.errors.title}
                                                required
                                            />

                                            {formik.values.answers.map((answer, index) => (
                                                <div key={index} className="d-flex align-items-center mb-3" style={{ position: "relative" }}>
                                                    <div className="flex-grow-1" style={{ position: "relative", zIndex: deletedAnswers.includes(index) ? 1 : 2 }}>
                                                        <CustomInput
                                                            label={`Answer ${index + 1}`}
                                                            id={`answers[${index}]`}
                                                            name={`answers[${index}]`}
                                                            value={formik.values.answers[index]}
                                                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                            onBlur={formik.handleBlur}
                                                            error={
                                                                !deletedAnswers.includes(index) &&
                                                                formik.errors.answers &&
                                                                    formik.touched.answers &&
                                                                    typeof formik.errors.answers[index] === "string"
                                                                    ? formik.errors.answers[index]
                                                                    : null
                                                            }
                                                            required
                                                            disabled={deletedAnswers.includes(index)}
                                                        />
                                                    </div>
                                                    {formik.values.answers.length > 2 && !deletedAnswers.includes(index) && (
                                                        <button
                                                            type="button"
                                                            className="btn text-danger ms-2"
                                                            onClick={() => handleDeleteAnswer(index)}
                                                            title="Delete"
                                                            style={{ zIndex: 2 }}
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                    {deletedAnswers.includes(index) && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: 0,
                                                                left: 0,
                                                                width: "100%",
                                                                height: "100%",
                                                                background: "rgba(0, 0, 0, 0.65)",
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                zIndex: 5,
                                                                borderRadius: "4px",
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUndoDeleteAnswer(index)}
                                                                className="simpleBtn"
                                                            >
                                                                <span>UNDO DELETE</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {formik.values.answers.length < 4 && (
                                                <div className="mb-4">
                                                    <button type="button" className="btn btn-outline-primary" onClick={addAnswer}>
                                                        Add Answer
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}


                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>


                                <TimezoneSelect
                                    label="Timezone"
                                    id="tz"
                                    name="tz"
                                    value={formik.values.tz}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.tz && formik.errors.tz}
                                />

                                <div className="row">
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="date"
                                            label="Start Date"
                                            name="startDate"
                                            value={formik.values.startDate}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.startDate && formik.errors.startDate}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="time"
                                            label="Start Time"
                                            name="startTime"
                                            value={formik.values.startTime}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.startTime && formik.errors.startTime}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="date"
                                            label="End Date"
                                            name="endDate"
                                            value={formik.values.endDate}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.endDate && formik.errors.endDate}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="time"
                                            label="End Time"
                                            name="endTime"
                                            value={formik.values.endTime}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.endTime && formik.errors.endTime}
                                        />
                                    </div>
                                </div>


                            </>
                        )}
                    </div>
                    <div className="col-md-6 border-right p-3 " >
                        <h6 className="mb-3 text-decoration-underline text-center">Optional Information </h6>
                        <div className="col-md-12">
                            <div className="d-flex flex-column align-items-center">
                                <div className="position-relative mb-3" style={{ position: "relative" }}>
                                    {croppedImage && !isImageDeleted ? (
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={croppedImage}
                                                alt="Image"
                                                className="img-fluid"
                                                style={{
                                                    width: "90%",
                                                    height: "120px",
                                                    objectFit: "contain",
                                                    border: "2px solid #ddd",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleDeleteImage}
                                                style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ) : croppedImage && isImageDeleted ? (
                                        <div className="position-relative">
                                            <img
                                                src={croppedImage}
                                                alt="Image"
                                                className="img-fluid"
                                                style={{
                                                    width: "100%",
                                                    height: "120px",
                                                    objectFit: "contain",
                                                    border: "2px solid #ddd",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                            />
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "rgba(0, 0, 0, 0.65)",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    zIndex: 5,
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={handleUndoDeleteImage}
                                                    className="simpleBtn"
                                                >
                                                    <span>UNDO DELETE</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center"
                                            style={{
                                                width: "510px",
                                                height: "100px",
                                                backgroundColor: "#f8f9fa",
                                                border: "2px dashed #ddd",
                                                borderRadius: "8px"
                                            }}
                                        >
                                            <FaImage size={40} color="#aaa" className="mb-2" />
                                            <small className="text-muted">Image Upload</small>
                                        </div>
                                    )}
                                    <label
                                        htmlFor="profileUpload"
                                        className="btn btn-sm btn-primary position-absolute"
                                        style={{
                                            bottom: "-15px",
                                            right: "43%",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            padding: "0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                        }}
                                    >
                                        <FaPlus size={12} />
                                    </label>
                                    <input
                                        type="file"
                                        id="profileUpload"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ display: "none" }}
                                    />
                                </div>
                            </div>

                            {/* CKEditor Section */}
                            <div className="my-4">
                                <label htmlFor="poll-description" className="form-label">
                                    Additional Information <small className="text-muted">(Optional)</small>
                                </label>
                                <div className="ckeditor-container" style={{ minHeight: "100px" }}>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={description}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            console.log(data)
                                            setFormikDescription(data);
                                        }}
                                        config={{
                                            placeholder: "Add a detailed description for your contest...",
                                            toolbar: {
                                                items: [
                                                    'heading', '|',
                                                    'bold', 'italic', 'underline', 'strikethrough', '|',
                                                    'fontColor', 'fontBackgroundColor', '|',
                                                    'bulletedList', 'numberedList', '|',
                                                    'alignment', '|',
                                                    'link', '|',
                                                    'undo', 'redo'
                                                ]
                                            },
                                            language: 'en'
                                        }}
                                        onReady={(editor) => {
                                            // Set the editor height
                                            editor.editing.view.change((writer) => {
                                                writer.setStyle(
                                                    'height',
                                                    '100px',
                                                    editor.editing.view.document.getRoot()
                                                );
                                            });
                                        }}
                                    />
                                </div>
                                {description && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Character count: {description.replace(/<[^>]*>/g, '').length}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12 ">
                        {step === 1 && (<div className="d-flex justify-content-end mt-3 modalBtnContainer">
                            <button type="button" className="btn btn-outline-secondary me-2" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary me-2">
                                Save as Draft
                            </button>
                            <button type="submit" className="btn btn-success">
                                Create and Schedule
                            </button>
                        </div>)
                        }
                        {step === 2 && (<div className="d-flex justify-content-end mt-3 gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setStep(1)}
                            >
                                Back
                            </button>

                            <button
                                type="submit"
                                className="btn btn-success"
                                disabled={
                                    !formik.values.startDate &&
                                    !formik.values.startTime &&
                                    !formik.values.endDate &&
                                    !formik.values.endTime &&
                                    !formik.values.tz
                                }
                            >
                                {formik.isSubmitting ? "Scheduling..." : "Schedule Poll"}
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={
                                    formik.values.startDate ||
                                    formik.values.startTime ||
                                    formik.values.endDate ||
                                    formik.values.endTime ||
                                    formik.values.tz
                                }
                                onClick={() => {
                                    formik.setFieldValue("startDate", "");
                                    formik.setFieldValue("startTime", "");
                                    formik.setFieldValue("endDate", "");
                                    formik.setFieldValue("endTime", "");
                                    formik.setFieldValue("tz", "");
                                }}
                            >
                                Start Now
                            </button>
                        </div>)
                        }

                    </div>
                </form>
            </Modal>
            <CropperModalAdmin
                heading="Poll Image"
                show={showModal}
                onClose={() => setShowModal(false)}
                image={image}
                onCrop={handleCrop}
                aspectRatio={1242 / 180}
            />
        </>
    );
};

export default CreateContest;